import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Spin, Form, Tabs, Result, Breadcrumb, message } from "antd";
import { BarChartOutlined, FieldTimeOutlined } from "@ant-design/icons";

import get from "lodash/get";
import map from "lodash/map";
import head from "lodash/head";
import find from "lodash/find";
import dayjs from "dayjs";
import requireAuth from "../../hocs/requireAuth";
import GraphSection from "../../components/graphs/graphSection";
import { fetchTimeGraphReport } from "../../redux/actions/reportActions";
import { fetchCompanies } from "../../redux/actions/companyActions";
import {
  fetchCompanyTemplates,
  updateOrderCompanyTemplates,
} from "../../redux/actions/templateActions";

import { getCurrentShift } from "../../utilities/helpers";

import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DraggableTabNode } from "./DraggableTabNode";
import { findIndex } from "lodash";
import { COMPANY_ADMIN_ROLE_ACCESS } from "../../constants";
import HeaderSelector from "../../components/graphs/timeGraph/headerSelector";
import { useLocation } from "react-router-dom";
import { getQueryParams } from "../../utilities/getAndOnParams";
import { SUPER_ADMIN_ROLE_ACCESS, ORGANISATION_ADMIN_ROLE_IDs, ORGANISATION_MANAGER_IDs, ORGANISATION_SUPERVISORS_IDs } from "../../constants/userRoles";


const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <BarChartOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <FieldTimeOutlined style={{ fontSize: "16px" }} />
              <span>Time</span>
            </>
          ),
        },
        {
          title: "Graph Report",
        },
      ]}
    />
  );
};

const initialState = {
  template: undefined,
  graph_report: undefined,
  interval: 60,
};

const TimeGraphReportScreen = () => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const { company_uuid, template_type, template } = getQueryParams(
    location.search,
  );
  const dispatch = useDispatch();
  const [config, setConfig] = useState(null);
  const [showEmpty, setShowEmpty] = useState(false);

  const [form] = Form.useForm();
  const [selectedTemplate, setSelectedTemplate] = useState(initialState);
  const [filter, setFilters] = useState({
    company: undefined,
    templateType: undefined,
    date: dayjs(),
    shift: undefined,
  });

  const [templates, setTemplates] = useState([]);

  const getReportGraph = (template, interval = 60, _shift = null) => {
    setLoading(true);
    const shift = _shift ? _shift : get(filter, "shift.id");
    const query = {
      uuid: get(template, "id"),
      shift_uuid: shift,
      interval: interval,
      date: get(filter, "date").format("YYYY-MM-DD"),
    };
    dispatch(fetchTimeGraphReport(query))
      .then((result) => {
        setSelectedTemplate({
          template,
          graph_report: get(result, "reports"),
          meta: get(result, "meta"),
          calculated_stats: get(result, "meta.calculated_stats"),
          interval,
          shift: shift,
          date: get(filter, "date").format("YYYY-MM-DD"),
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const setinitialCompany = (company) => {
    setFilters({
      ...filter,
      templateType: undefined,
      company,
    });
    setConfig({
      ...config,
      companies: [company],
      templates: get(company, "template_access_list"),
      shifts: get(company, "company_shifts"),
    });
    form.setFieldsValue({
      template: undefined,
      Company: get(company, "id"),
      Shift: get(company, "company_shifts"),
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (result.length === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getCompanyTemplates = (company_uuid, template_type) => {
    setLoading(true);
    dispatch(
      fetchCompanyTemplates(company_uuid, {
        type: template_type,
        excludePaused: true
      }),
    )
      .then((result) => {
        setTemplates(result);
        if (result.length > 0) {
          if (get(selectedTemplate, "template")) {
            getReportGraph(
              get(selectedTemplate, "template"),
              60,
              get(filter, "shift.id"),
            );
          } else {
            getReportGraph(head(result), 60, get(filter, "shift.id"));
          }
          setShowEmpty(false);
        } else {
          setSelectedTemplate(initialState);
          setShowEmpty(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const sendReorderRequest = (payload) => {
    setLoading(true);
    dispatch(
      updateOrderCompanyTemplates(get(filter, "company.id"), payload, {
        type: get(filter, "templateType"),
      }),
    )
      .then(() => {
        message.success("Templates sorted successfully");
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const sensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 },
  });

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setTemplates((prev) => {
        const activeIndex = findIndex(
          prev,
          (i) => get(i, "id") === get(active, "id"),
        );
        const overIndex = findIndex(
          prev,
          (i) => get(i, "id") === get(over, "id"),
        );
        const newTemplates = arrayMove(prev, activeIndex, overIndex);
        const newTemplateUuids = map(newTemplates, (i) => get(i, "id"));
        const payload = {
          template_uuid: newTemplateUuids,
        };
        sendReorderRequest(payload);
        return newTemplates;
      });
      getReportGraph(
        find(templates, ["id", active.id]),
        60,
        get(filter, "shift.id"),
      );
    }
  };
  const paramsFormHandler = () => {
    setLoading(true);

    dispatch(fetchCompanies())
      .then((companies) => {
        const company = find(companies, ["id", company_uuid]);

        if (!company) {
          message.error("Company not found from URL param.");
          setLoading(false);
          return Promise.reject(new Error("Company not found"));
        }

        const currentShift = getCurrentShift(get(company, "company_shifts"));
        form.setFieldsValue({
          Company: company_uuid,
          template: parseInt(Number(template_type)),
          date: dayjs(),
          shift: get(currentShift, "id"),
        });

        setConfig((prev) => ({
          ...prev,
          companies,
          templates: get(company, "template_access_list"),
          shifts: get(company, "company_shifts"),
        }));

        return { company };
      })
      .then(({ company }) => {
        const currentShift = getCurrentShift(get(company, "company_shifts"));
        return dispatch(
          fetchCompanyTemplates(get(company, "id"), {
            type: Number(template_type),
          }),
        ).then((templatesResult) => {
          setTemplates(templatesResult);
          const templateToLoad = find(templatesResult, ["id", template]);

          if (!templateToLoad) {
            message.error("Template not found from URL param.");
            setLoading(false);
            return Promise.reject(new Error("Template not found"));
          }

          getReportGraph(templateToLoad, 60, get(currentShift, "id"));

          setFilters((prev) => ({
            ...prev,
            company,
            templateType: Number(template_type),
            date: dayjs(),
            shift: currentShift,
          }));

          setLoading(false);
        });
      })
      .catch((error) => {
        console.error("Error in paramsFormHandler:", error);
        message.error("An error occurred while fetching data.");
        setLoading(false);
      });
  };
  useEffect(() => {
    getCompanies();
    if (company_uuid && template_type && template) {
      paramsFormHandler();
    }
  }, []);
  return (
    <div className="p-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <div>
          <PageBreadcrumb />
        </div>
        <div></div>
      </div>
      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto scrollbar-hidden pb-5 min-w-full ">
        <Form form={form}>
          <Spin spinning={loading}>
            <div className="w-fit px-8 py-6">
              <HeaderSelector
                form={form}
                filter={filter}
                setFilters={setFilters}
                config={config}
                setConfig={setConfig}
                setSelectedTemplate={setSelectedTemplate}
                setTemplates={setTemplates}
                getCompanyTemplates={() => {
                  getCompanyTemplates(
                    get(filter, "company.id"),
                    get(filter, "templateType"),
                  );
                }}
              />
            </div>
            {get(templates, "length") > 0 && (
              <div className="px-8 overflow-y-auto">
                <Tabs
                  tabBarGutter={10}
                  tabPosition={"left"}
                  type="card"
                  destroyOnHidden
                  activeKey={get(selectedTemplate, "template.id")}
                  onTabClick={(key) => {
                    getReportGraph(
                      find(templates, ["id", key]),
                      60,
                      get(filter, "shift.id"),
                    );
                  }}
                  items={map(templates, (template) => ({
                    key: get(template, "id"),
                    label: get(template, "name"),
                    children: (
                      <GraphSection
                        filter={filter}
                        setLoading={setLoading}
                        selectedTemplate={selectedTemplate}
                        onChangeParams={getReportGraph}
                      />
                    ),
                  }))}
                  renderTabBar={(tabBarProps, DefaultTabBar) => (
                    <DndContext
                      sensors={[sensor]}
                      onDragEnd={onDragEnd}
                      collisionDetection={closestCenter}
                    >
                      <SortableContext
                        items={map(templates, (template) =>
                          get(template, "id"),
                        )}
                        strategy={verticalListSortingStrategy}
                      >
                        <DefaultTabBar {...tabBarProps}>
                          {(node) => (
                            <DraggableTabNode
                              {...get(node, "props")}
                              key={get(node, "key")}
                            >
                              {node}
                            </DraggableTabNode>
                          )}
                        </DefaultTabBar>
                      </SortableContext>
                    </DndContext>
                  )}
                  onChange={() => { }}
                />
              </div>
            )}
            {showEmpty && (
              <div className="px-8 py-8">
                <Result
                  title={`No template exists...!`}
                  subTitle="Please try again with another company or template type."
                  status="404"
                />
              </div>
            )}
          </Spin>
        </Form>
      </div>
    </div>
  );
};

export default requireAuth(TimeGraphReportScreen, [
  ...SUPER_ADMIN_ROLE_ACCESS,
  ...ORGANISATION_ADMIN_ROLE_IDs,
  ...ORGANISATION_MANAGER_IDs,
  ...ORGANISATION_SUPERVISORS_IDs
]);
